var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { ReflectUtils } from "@dooboostore/core";
import { buildSwcParameterArgs, buildCommonKindValues } from "./parameter.js";
const INTERSECTION_OBSERVER_METADATA_KEY = Symbol.for("simple-web-component:intersection-observer");
const applyIntersectionObserver = (selector, options, targetObj, propertyKey) => {
  const constructor = targetObj.constructor;
  let observers = ReflectUtils.getOwnMetadata(INTERSECTION_OBSERVER_METADATA_KEY, constructor);
  if (!observers) {
    observers = [];
    ReflectUtils.defineMetadata(INTERSECTION_OBSERVER_METADATA_KEY, observers, constructor);
  }
  observers.push({ propertyKey, selector, options });
};
const resolveIntersectionObserverArgs = (selectorOrOptions, maybeOptions, extra) => {
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return { selector: selectorOrOptions, options: { ...maybeOptions ?? {}, ...extra } };
  }
  return { selector: "$this", options: { ...selectorOrOptions ?? {}, ...extra } };
};
const dispatchIntersectionObserver = (selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, extra) => {
  if ((typeof maybeOptionsOrPropertyKey === "string" || typeof maybeOptionsOrPropertyKey === "symbol") && descriptor !== void 0) {
    applyIntersectionObserver("$this", { ...extra }, selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey);
    return;
  }
  const { selector, options } = resolveIntersectionObserverArgs(
    selectorOrOptionsOrTarget,
    maybeOptionsOrPropertyKey,
    extra
  );
  return (targetObj, propertyKey) => {
    applyIntersectionObserver(selector, options, targetObj, propertyKey);
  };
};
function intersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, {});
}
function intersectionObserverDelegateLight(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "light", delegate: true });
}
function intersectionObserverDelegateShadow(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "shadow", delegate: true });
}
function intersectionObserverDelegateAll(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "all", delegate: true });
}
function intersectionObserverDelegate(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "auto", delegate: true });
}
function intersectionObserverLight(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "light" });
}
function intersectionObserverShadow(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "shadow" });
}
function intersectionObserverAll(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchIntersectionObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "all" });
}
function getIntersectionObserverMetadata(target) {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(INTERSECTION_OBSERVER_METADATA_KEY, constructor).flat();
}
import { SwcUtils } from "../utils/Utils.js";
class IntersectionObserverLifeCycler {
  constructor() {
    __publicField(this, "removeObserverCallbacksMap", /* @__PURE__ */ new WeakMap());
  }
  onConnected(helperHostSet) {
    const inst = helperHostSet.$this;
    const currentWin = helperHostSet.$w;
    const removeObserverCallbacks = [];
    this.removeObserverCallbacksMap.set(inst, removeObserverCallbacks);
    const allMeta = getIntersectionObserverMetadata(inst) ?? [];
    if (allMeta.length === 0) return;
    const root = inst.shadowRoot || inst;
    for (const m of allMeta) {
      if (m.options.removeObserver) removeObserverCallbacks.push({ fn: m.options.removeObserver, target: root, opts: m.options });
    }
    const buildCallback = (metas) => {
      return (entries, obs) => {
        for (const m of metas) {
          let matchedEls;
          if (m.options.delegate && typeof m.selector === "string") {
            const isThis = m.selector === "$this" || m.selector === "";
            matchedEls = entries.map((e) => e.target).filter((el) => {
              if (!el || el.nodeType !== 1) return false;
              if (isThis) return true;
              return el.matches?.(m.selector) || !!el.closest?.(m.selector);
            });
          } else {
            matchedEls = entries.map((e) => e.target).filter((t) => t && t.nodeType === 1);
          }
          if (matchedEls.length === 0) continue;
          const opts = m.options;
          void (async () => {
            const helper = SwcUtils.getHelperAndHostSet(inst);
            if (opts.filter && !await opts.filter(matchedEls, { currentThis: inst, helper })) return;
            const common = buildCommonKindValues(inst, helperHostSet.$w);
            const legacyArgs = [matchedEls, entries, obs, { ...common.hostSet, $root: root }];
            const buildArgs = (beforeReturn) => buildSwcParameterArgs(inst, m.propertyKey, {
              ...common,
              intersectionObserverBeforeReturn: beforeReturn
            }, [...legacyArgs, beforeReturn]);
            let args = buildArgs(void 0);
            if (opts.before) {
              const br = await opts.before(matchedEls, { currentThis: inst, helper });
              args = buildArgs(br);
            }
            let result, error;
            try {
              result = await inst[m.propertyKey](...args);
            } catch (e) {
              error = e;
            } finally {
              if (opts.finally) await opts.finally(matchedEls, { currentThis: inst, helper }, { args, result, error });
            }
            if (error) throw error;
          })().catch((e) => console.error("[SWC] intersectionObserver handler error:", e));
        }
      };
    };
    const groups = /* @__PURE__ */ new Map();
    for (const m of allMeta) {
      const obsOptions = {
        root: m.options.intersectionRoot ?? null,
        rootMargin: m.options.rootMargin,
        threshold: m.options.threshold
      };
      const key = JSON.stringify([obsOptions.root, obsOptions.rootMargin, obsOptions.threshold]);
      if (!groups.has(key)) groups.set(key, { options: obsOptions, metas: [] });
      groups.get(key).metas.push(m);
    }
    const intersectionObserverSet = [];
    for (const [, group] of groups) {
      const callback = buildCallback(group.metas);
      const observeTargets = [];
      for (const m of group.metas) {
        if (m.options.delegate && typeof m.selector === "string") {
          observeTargets.push({ target: m.selector, delegate: true, delegateRoot: m.options.root, callback });
          continue;
        }
        let targets = [];
        if (typeof m.selector === "function") {
          const res = m.selector(inst, helperHostSet);
          if (res instanceof currentWin.HTMLElement) targets = [res];
          else if (res instanceof currentWin.NodeList) targets = Array.from(res).filter((e) => e instanceof currentWin.HTMLElement);
          else if (Array.isArray(res)) targets = res.filter((e) => e instanceof currentWin.HTMLElement);
        } else if (typeof m.selector === "string") {
          if (m.selector === "$this" || m.selector === "") {
            targets = [inst];
          } else {
            const r = m.options.root || "auto";
            const scopes = [];
            if (r === "auto") scopes.push(inst.shadowRoot || inst);
            else if (r === "light") scopes.push(inst);
            else if (r === "shadow" && inst.shadowRoot) scopes.push(inst.shadowRoot);
            else if (r === "all") {
              scopes.push(inst);
              if (inst.shadowRoot) scopes.push(inst.shadowRoot);
            }
            for (const s of scopes) {
              const found = s?.querySelectorAll?.(m.selector);
              if (found?.length) targets.push(...Array.from(found));
            }
          }
        }
        for (const el of targets) observeTargets.push({ target: el, callback });
      }
      intersectionObserverSet.push({ options: group.options, observeTargets });
    }
    return { intersectionObserverSet };
  }
  onDisconnected(helperHostSet) {
    const inst = helperHostSet.$this;
    for (const cb of this.removeObserverCallbacksMap.get(inst) ?? []) {
      try {
        cb.fn(cb.target, cb.opts);
      } catch (e) {
        console.error("[SWC] removeObserver error:", e);
      }
    }
    this.removeObserverCallbacksMap.delete(inst);
  }
}
export {
  INTERSECTION_OBSERVER_METADATA_KEY,
  IntersectionObserverLifeCycler,
  getIntersectionObserverMetadata,
  intersectionObserver,
  intersectionObserverAll,
  intersectionObserverDelegate,
  intersectionObserverDelegateAll,
  intersectionObserverDelegateLight,
  intersectionObserverDelegateShadow,
  intersectionObserverLight,
  intersectionObserverShadow
};
//# sourceMappingURL=intersectionObserver.js.map
